/**
 * 🚪 SAS DE DÉCOMPRESSION - Animation d'entrée dans le cockpit
 * Transition immersive style vaisseau spatial
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Rocket, CheckCircle, AlertTriangle } from 'lucide-react';

// Séquence d'animation du sas
const AIRLOCK_SEQUENCE = [
  { id: 'auth', label: 'Authentification...', icon: Shield, duration: 800 },
  { id: 'scan', label: 'Scan biométrique...', icon: AlertTriangle, duration: 600 },
  { id: 'decomp', label: 'Décompression...', icon: Rocket, duration: 1000 },
  { id: 'access', label: 'Accès autorisé', icon: CheckCircle, duration: 500 },
];

// Sons du sas (Web Audio API)
const playAirlockSound = (type) => {
  if (typeof window === 'undefined' || !window.AudioContext) return;
  
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    
    switch (type) {
      case 'door_close':
        // Son de porte qui se ferme
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.connect(gain1);
        gain1.connect(ctx.destination);
        osc1.type = 'sawtooth';
        osc1.frequency.setValueAtTime(150, ctx.currentTime);
        osc1.frequency.exponentialRampToValueAtTime(50, ctx.currentTime + 0.3);
        gain1.gain.setValueAtTime(0.3, ctx.currentTime);
        gain1.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc1.start(ctx.currentTime);
        osc1.stop(ctx.currentTime + 0.3);
        break;
        
      case 'scan':
        // Son de scan
        const osc2 = ctx.createOscillator();
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);
        osc2.type = 'sine';
        osc2.frequency.setValueAtTime(800, ctx.currentTime);
        osc2.frequency.setValueAtTime(1200, ctx.currentTime + 0.1);
        osc2.frequency.setValueAtTime(800, ctx.currentTime + 0.2);
        gain2.gain.setValueAtTime(0.15, ctx.currentTime);
        gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        osc2.start(ctx.currentTime);
        osc2.stop(ctx.currentTime + 0.3);
        break;
        
      case 'decompress':
        // Son de décompression (sifflement)
        const osc3 = ctx.createOscillator();
        const gain3 = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        osc3.connect(filter);
        filter.connect(gain3);
        gain3.connect(ctx.destination);
        osc3.type = 'sawtooth';
        filter.type = 'lowpass';
        filter.frequency.setValueAtTime(2000, ctx.currentTime);
        filter.frequency.exponentialRampToValueAtTime(200, ctx.currentTime + 1);
        osc3.frequency.setValueAtTime(100, ctx.currentTime);
        gain3.gain.setValueAtTime(0.2, ctx.currentTime);
        gain3.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 1);
        osc3.start(ctx.currentTime);
        osc3.stop(ctx.currentTime + 1);
        break;
        
      case 'door_open':
        // Son de porte qui s'ouvre
        const osc4 = ctx.createOscillator();
        const gain4 = ctx.createGain();
        osc4.connect(gain4);
        gain4.connect(ctx.destination);
        osc4.type = 'sine';
        osc4.frequency.setValueAtTime(200, ctx.currentTime);
        osc4.frequency.exponentialRampToValueAtTime(600, ctx.currentTime + 0.4);
        gain4.gain.setValueAtTime(0.25, ctx.currentTime);
        gain4.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
        osc4.start(ctx.currentTime);
        osc4.stop(ctx.currentTime + 0.5);
        break;
        
      case 'welcome':
        // Mélodie de bienvenue
        const frequencies = [523.25, 659.25, 783.99, 1046.50];
        let time = ctx.currentTime;
        frequencies.forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.type = 'sine';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.15, time);
          gain.gain.exponentialRampToValueAtTime(0.01, time + 0.2);
          osc.start(time);
          osc.stop(time + 0.2);
          time += 0.15;
        });
        break;
    }
  } catch (e) {
    console.warn('Sound error:', e);
  }
};

// Composant de porte du sas
const AirlockDoor = ({ position, isOpen }) => (
  <motion.div
    className={`absolute ${position === 'left' ? 'left-0' : 'right-0'} top-0 bottom-0 w-1/2 z-50`}
    initial={{ x: 0 }}
    animate={{ x: isOpen ? (position === 'left' ? '-100%' : '100%') : 0 }}
    transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
    style={{
      background: 'linear-gradient(135deg, #1a1a2e 0%, #0f0f1a 100%)',
      borderRight: position === 'left' ? '4px solid #00d4ff' : 'none',
      borderLeft: position === 'right' ? '4px solid #00d4ff' : 'none',
    }}
  >
    {/* Détails de la porte */}
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="w-32 h-2 rounded-full"
            style={{
              background: isOpen 
                ? 'linear-gradient(90deg, #00d4ff 0%, transparent 100%)'
                : 'linear-gradient(90deg, #ff0066 0%, #ff0066 100%)',
              opacity: 0.6,
            }}
            animate={{
              opacity: isOpen ? [0.6, 0.2] : [0.6, 1, 0.6],
            }}
            transition={{
              duration: isOpen ? 0.5 : 1,
              repeat: isOpen ? 0 : Infinity,
              delay: i * 0.1,
            }}
          />
        ))}
      </div>
    </div>
    
    {/* Symbole danger */}
    <div className={`absolute ${position === 'left' ? 'right-8' : 'left-8'} top-1/2 -translate-y-1/2`}>
      <motion.div
        animate={{ rotate: isOpen ? 0 : [0, 5, -5, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <AlertTriangle className={`w-12 h-12 ${isOpen ? 'text-green-400' : 'text-yellow-400'}`} />
      </motion.div>
    </div>
  </motion.div>
);

// Écran de statut du sas
const AirlockStatus = ({ currentStep, steps }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center z-40 pointer-events-none">
    {/* Logo / Titre */}
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      className="mb-8"
    >
      <div className="text-6xl mb-4">🚀</div>
      <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
        AIRLOCK SYSTEM
      </h1>
      <p className="text-gray-500 text-center mt-2">Sas de décompression</p>
    </motion.div>
    
    {/* Étapes */}
    <div className="space-y-3">
      {steps.map((step, idx) => {
        const Icon = step.icon;
        const isActive = idx === currentStep;
        const isComplete = idx < currentStep;
        
        return (
          <motion.div
            key={step.id}
            initial={{ opacity: 0, x: -20 }}
            animate={{ 
              opacity: isComplete || isActive ? 1 : 0.3,
              x: 0,
            }}
            transition={{ delay: idx * 0.1 }}
            className={`flex items-center gap-4 px-6 py-3 rounded-xl border ${
              isComplete 
                ? 'border-green-500/50 bg-green-500/10' 
                : isActive 
                  ? 'border-cyan-500/50 bg-cyan-500/10'
                  : 'border-gray-700 bg-gray-800/30'
            }`}
          >
            <motion.div
              animate={isActive ? { rotate: 360 } : {}}
              transition={{ duration: 1, repeat: isActive ? Infinity : 0, ease: 'linear' }}
            >
              <Icon className={`w-6 h-6 ${
                isComplete ? 'text-green-400' : isActive ? 'text-cyan-400' : 'text-gray-600'
              }`} />
            </motion.div>
            <span className={`text-lg font-medium ${
              isComplete ? 'text-green-400' : isActive ? 'text-cyan-400' : 'text-gray-600'
            }`}>
              {step.label}
            </span>
            {isComplete && (
              <CheckCircle className="w-5 h-5 text-green-400 ml-auto" />
            )}
            {isActive && (
              <motion.div
                className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full ml-auto"
                animate={{ rotate: 360 }}
                transition={{ duration: 0.8, repeat: Infinity, ease: 'linear' }}
              />
            )}
          </motion.div>
        );
      })}
    </div>
  </div>
);

// Composant principal du sas
const AirlockTransition = ({ isActive, onComplete, userName }) => {
  const [currentStep, setCurrentStep] = useState(-1);
  const [doorsOpen, setDoorsOpen] = useState(false);
  
  useEffect(() => {
    if (!isActive) {
      setCurrentStep(-1);
      setDoorsOpen(false);
      return;
    }
    
    // Démarrer la séquence
    let stepIndex = 0;
    
    // Son de fermeture des portes
    playAirlockSound('door_close');
    
    const runSequence = () => {
      if (stepIndex >= AIRLOCK_SEQUENCE.length) {
        // Ouvrir les portes
        playAirlockSound('door_open');
        setDoorsOpen(true);
        
        // Jouer le son de bienvenue après un délai
        setTimeout(() => {
          playAirlockSound('welcome');
        }, 500);
        
        // Terminer l'animation
        setTimeout(() => {
          onComplete?.();
        }, 1200);
        return;
      }
      
      const step = AIRLOCK_SEQUENCE[stepIndex];
      setCurrentStep(stepIndex);
      
      // Jouer les sons correspondants
      if (step.id === 'scan') playAirlockSound('scan');
      if (step.id === 'decomp') playAirlockSound('decompress');
      
      stepIndex++;
      setTimeout(runSequence, step.duration);
    };
    
    // Démarrer après un court délai
    setTimeout(runSequence, 500);
  }, [isActive, onComplete]);
  
  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] overflow-hidden"
          style={{
            background: 'radial-gradient(ellipse at center, #0a0a1a 0%, #000 100%)',
          }}
        >
          {/* Étoiles de fond */}
          <div className="absolute inset-0">
            {[...Array(50)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1 h-1 bg-white rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  opacity: [0.3, 0.8, 0.3],
                  scale: [1, 1.5, 1],
                }}
                transition={{
                  duration: 2 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
          
          {/* Portes du sas */}
          <AirlockDoor position="left" isOpen={doorsOpen} />
          <AirlockDoor position="right" isOpen={doorsOpen} />
          
          {/* Statut */}
          <AirlockStatus currentStep={currentStep} steps={AIRLOCK_SEQUENCE} />
          
          {/* Message de bienvenue */}
          <AnimatePresence>
            {doorsOpen && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                className="absolute bottom-20 left-0 right-0 text-center z-50"
              >
                <motion.p
                  className="text-2xl font-bold text-green-400"
                  animate={{ opacity: [1, 0.7, 1] }}
                  transition={{ duration: 1, repeat: Infinity }}
                >
                  🚀 Bienvenue dans le cockpit, {userName || 'Capitaine'} !
                </motion.p>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Lignes de scan */}
          {currentStep === 1 && (
            <motion.div
              className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent z-50"
              initial={{ top: '20%' }}
              animate={{ top: '80%' }}
              transition={{ duration: 0.5, repeat: 2, repeatType: 'reverse' }}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AirlockTransition;
export { playAirlockSound };
